import { useEffect, useState } from "react";

import Loader from "@syntensor/common/components/loader";
import { filterItemsBySearch } from "@syntensor/common/utils/search_utils";
import SearchForm from "@syntensor/common/components/search_form";
import {
  fetchGroupData,
  fetchProjects,
  fetchProjectsByGroup,
} from "@syntensor/common/data/fetch_data";
import getCopy from "@syntensor/common/data/copy";
import { IProject } from "@syntensor/common/types";

import ProjectBox from "./project_box";

import classes from "./projects.module.css";

import Button from "@syntensor/common/components/button";
import { Link, useRouteMatch } from "react-router-dom";
import { getCurrentUserSub, IsUserInAdminGroup } from "../auth/auth_context";

interface IGroupMatchParams {
  0: string;
  groupId: string;
}

interface IGroupData {
  awsSub: string;
  roleId: number;
}

export default function Projects() {
  const [isLoading, setIsLoading] = useState(true);
  const [projects, setProjects] = useState<IProject[]>([]);
  const [search, setSearch] = useState<string>("");
  const [projectName, setProjectName] = useState<string>("");
  const [isUserGroupSuperAdmin, setIsUserGroupSuperAdmin] = useState(false);
  const [isUserGroupAdmin, setIsUserGroupAdmin] = useState(false);
  const [userSub] = useState<string>(getCurrentUserSub() ?? "");
  const [isUserInAdminGroup] = useState(IsUserInAdminGroup());

  const { params } = useRouteMatch<IGroupMatchParams>();
  const { groupId } = params;

  async function asyncFetch() {
    try {
      setIsLoading(true);

      if (process.env.REACT_APP_PROJECT_UI === "1") {
        const projectData = await fetchProjectsByGroup(groupId);
        setProjects(projectData);

        if (isUserInAdminGroup) {
          setIsUserGroupAdmin(true);
          setIsUserGroupSuperAdmin(true);
        } else {
          const groupData = await fetchGroupData(groupId);
          const superAdmin = groupData.group.some(
            (item: IGroupData) => item.awsSub === userSub && item.roleId === 1
          );
          const admin = groupData.group.some(
            (item: IGroupData) => item.awsSub === userSub && item.roleId === 2
          );

          setIsUserGroupAdmin(admin);
          setIsUserGroupSuperAdmin(superAdmin);
        }

        if (sessionStorage.getItem("groupName")) {
          setProjectName(sessionStorage.getItem("groupName")!);
        } else {
          setProjectName(getCopy("projects_header_team"));
        }
      } else {
        setProjects(await fetchProjects());
        setProjectName(getCopy("projects_header_team"));
      }

      setIsLoading(false);
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    asyncFetch();
  }, []);

  const handleSearch = (search: string) => {
    setSearch(search);
  };

  const handleChange = (newSearch: string) => {
    if (search && newSearch === "") {
      //  cleared input box
      setSearch("");
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  //  we might be rendering subset of the components
  //  due to search term being input in the search form
  const filteredProjects = search
    ? (filterItemsBySearch(projects, search) as IProject[])
    : projects;

  const hasResults = filteredProjects && filteredProjects.length > 0;

  return (
    <div className={classes.projects}>
      <div className={classes.header}>
        <h3 className={classes.headerTitle} data-testid="projects_title">
          {process.env.REACT_APP_PROJECT_UI === "1" ? (
            <>
              <Link to="/groups/">Your Groups</Link> / {projectName} /{" "}
              <span
                dangerouslySetInnerHTML={{
                  __html: getCopy("projects_overview_title"),
                }}
              />
            </>
          ) : (
            <span
              dangerouslySetInnerHTML={{
                __html: getCopy("projects_overview_title"),
              }}
            />
          )}
        </h3>
        <div className={classes.headerButtons}>
          {(isUserGroupAdmin || isUserGroupSuperAdmin) &&
          process.env.REACT_APP_PROJECT_UI === "1" ? (
            <>
              <Button to={`/groups/${groupId}/edit`} role="subtle">
                {getCopy("projects_button_configureGroup")}
              </Button>
            </>
          ) : (
            <p>&nbsp;</p>
          )}

          {(isUserGroupAdmin || isUserGroupSuperAdmin) &&
            process.env.REACT_APP_PROJECT_UI === "1" && (
              <Button to={`/projects/${groupId}/create`} role="subtle">
                {getCopy("projects_overview_new-project")}
              </Button>
            )}

          {process.env.REACT_APP_PROJECT_UI === "0" && <div>&nbsp;</div>}
        </div>
      </div>
      <div className={classes.search}>
        <SearchForm
          onSearch={handleSearch}
          onChange={handleChange}
          placeholder={getCopy("projects_overview_search-placeholder")}
          debounceDelay={0}
          name="search-projects"
          minChars={1}
          size="small"
        />
      </div>
      <div className={classes.list}>
        <ul className={classes.grid}>
          {filteredProjects &&
            filteredProjects.map((project) => {
              return (
                <li key={project.id}>
                  <ProjectBox
                    {...project}
                    onDelete={project.roleId === 1 ? asyncFetch : null}
                    onClone={project.roleId === 1}
                  />
                </li>
              );
            })}
        </ul>
        {!hasResults && <div>{getCopy("projects_overview_empty-result")}</div>}
      </div>
    </div>
  );
}
